import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { DEFAULT_GC_TIME, DEFAULT_STALE_TIME } from '@/client/constants';
import { useClientStore } from '@/client/stores/client';
import { withErrorHandling } from '@/client/utils/errors';
import type {
  ListingEngagement,
  ListingPackage,
  ListingTransactionPage,
  ListingTransactionsParams,
  PromotedListing,
  PromotedListingPage,
  PromotedListingsParams,
} from '@/client/types/promotion';

const LISTINGS = '/api/admin/promotions/listings';
const ENTITIES = '/api/admin/entities';

export const promotedListingKeys = {
  all: ['promoted-listings'] as const,
  list: (params: PromotedListingsParams) => [...promotedListingKeys.all, 'list', params] as const,
  detail: (eid: number) => [...promotedListingKeys.all, 'detail', eid] as const,
  packages: (eid: number) => [...promotedListingKeys.all, 'packages', eid] as const,
  transactions: (eid: number, params: ListingTransactionsParams) =>
    [...promotedListingKeys.all, 'transactions', eid, params] as const,
  engagement: (eid: number, year: number) => [...promotedListingKeys.all, 'engagement', eid, year] as const,
};

/** ff-api wraps payloads as `{ status, data }`. */
const unwrapEnvelope = <T,>(raw: any): T =>
  raw != null && typeof raw === 'object' && 'status' in raw && 'data' in raw ? raw.data : raw;

const toQueryString = (params: object) => {
  const search = Object.entries(params).reduce<Record<string, string>>((acc, [key, value]) => {
    if (value === undefined || value === null || value === '') return acc;
    // `risk` is the only repeatable filter and the API reads it as CSV.
    acc[key] = Array.isArray(value) ? value.join(',') : String(value);
    return acc;
  }, {});
  const qs = new URLSearchParams(search).toString();
  return qs ? `?${qs}` : '';
};

const useRequest = () => useClientStore((state) => state.request);

// QUERY FUNCTIONS
const useListingsFn = () => {
  const request = useRequest();
  return withErrorHandling(async (params: PromotedListingsParams): Promise<PromotedListingPage> => {
    const raw = unwrapEnvelope<any>(await request<any>(`${LISTINGS}${toQueryString(params)}`));
    return {
      data: raw?.data ?? [],
      page: raw?.page ?? params.page ?? 1,
      page_size: raw?.page_size ?? params.page_size ?? 12,
      total: raw?.total ?? 0,
    };
  }, 'Failed to fetch promoted listings');
};

const useListingFn = () => {
  const request = useRequest();
  return withErrorHandling(async (eid: number): Promise<PromotedListing> => {
    return unwrapEnvelope<PromotedListing>(await request<any>(`${LISTINGS}/${eid}`));
  }, 'Failed to fetch this promoted listing');
};

const usePackagesFn = () => {
  const request = useRequest();
  return withErrorHandling(async (eid: number): Promise<ListingPackage[]> => {
    const raw = unwrapEnvelope<any>(await request<any>(`${ENTITIES}/${eid}/promotions`));
    return Array.isArray(raw) ? raw : [];
  }, 'Failed to fetch the package history');
};

const useTransactionsFn = () => {
  const request = useRequest();
  return withErrorHandling(
    async (eid: number, params: ListingTransactionsParams): Promise<ListingTransactionPage> => {
      const raw = unwrapEnvelope<any>(
        await request<any>(`${ENTITIES}/${eid}/promotion-transactions${toQueryString(params)}`),
      );
      return {
        data: raw?.data ?? [],
        page: raw?.page ?? params.page ?? 1,
        page_size: raw?.page_size ?? params.page_size ?? 20,
        total: raw?.total ?? 0,
        summary: {
          total_spend: raw?.summary?.total_spend ?? 0,
          refunded: raw?.summary?.refunded ?? 0,
          paid_count: raw?.summary?.paid_count ?? 0,
          first_spend_date: raw?.summary?.first_spend_date ?? null,
          last_spend_date: raw?.summary?.last_spend_date ?? null,
        },
      };
    },
    'Failed to fetch the transaction history',
  );
};

const useEngagementFn = () => {
  const request = useRequest();
  return withErrorHandling(async (eid: number, year: number): Promise<ListingEngagement> => {
    const raw = unwrapEnvelope<any>(await request<any>(`${ENTITIES}/${eid}/engagement/monthly?year=${year}`));
    return { eid: raw?.eid ?? eid, year: raw?.year ?? year, series: raw?.series ?? [] };
  }, 'Failed to fetch engagement');
};

// QUERY HOOKS
/** Promoted listings with server-side search, filters, sort and paging. */
export const usePromotedListingsQuery = (params: PromotedListingsParams) => {
  const queryFn = useListingsFn();
  return useQuery({
    queryKey: promotedListingKeys.list(params),
    queryFn: () => queryFn(params),
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
    placeholderData: keepPreviousData,
  });
};

/** One listing's rollup header. */
export const usePromotedListingQuery = (eid: number | null) => {
  const queryFn = useListingFn();
  return useQuery({
    queryKey: promotedListingKeys.detail(eid ?? 0),
    queryFn: () => queryFn(eid as number),
    enabled: Boolean(eid),
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
  });
};

/** Every promotion package ever bought for a listing. */
export const useListingPackagesQuery = (eid: number | null) => {
  const queryFn = usePackagesFn();
  return useQuery({
    queryKey: promotedListingKeys.packages(eid ?? 0),
    queryFn: () => queryFn(eid as number),
    enabled: Boolean(eid),
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
  });
};

/** Purchase/refund history. `summary` covers all matches, not just this page. */
export const useListingTransactionsQuery = (eid: number | null, params: ListingTransactionsParams = {}) => {
  const queryFn = useTransactionsFn();
  return useQuery({
    queryKey: promotedListingKeys.transactions(eid ?? 0, params),
    queryFn: () => queryFn(eid as number, params),
    enabled: Boolean(eid),
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
    placeholderData: keepPreviousData,
  });
};

/** Monthly engagement counts per engagement type for one calendar year. */
export const useListingEngagementQuery = (eid: number | null, year: number) => {
  const queryFn = useEngagementFn();
  return useQuery({
    queryKey: promotedListingKeys.engagement(eid ?? 0, year),
    queryFn: () => queryFn(eid as number, year),
    enabled: Boolean(eid),
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
    placeholderData: keepPreviousData,
  });
};
